import { useState, useEffect } from 'react';
import { useStore } from '@/store';
import { 
  getCurrentHourInZone, 
  getWorkStatus, 
  findBestOverlapTimes 
} from '@/lib/timezone';

export default function OverlapFinder() {
  const { me, people } = useStore();
  const [currentHour, setCurrentHour] = useState(getCurrentHourInZone(me.timezone));
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHour(getCurrentHourInZone(me.timezone));
    }, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [me.timezone]);
  
  if (people.length === 0) {
    return null;
  }
  
  // Find overlaps including myself
  const allPeople = [
    { workStart: me.workHourStart, workEnd: me.workHourEnd, timezone: me.timezone, name: 'You' },
    ...people.map(p => ({ 
      workStart: p.workHourStart, 
      workEnd: p.workHourEnd, 
      timezone: p.timezone,
      name: p.name 
    })),
  ];
  
  const overlaps = findBestOverlapTimes(
    allPeople.map(p => ({ workStart: p.workStart, workEnd: p.workEnd, timezone: p.timezone })),
    me.timezone
  );
  
  // Get current status for each person
  const statusSummary = {
    working: [] as string[],
    soon: [] as string[],
    offline: [] as string[],
    sleeping: [] as string[],
  };
  
  // Add myself
  const myStatus = getWorkStatus(currentHour, me.workHourStart, me.workHourEnd);
  statusSummary[myStatus].push('You');
  
  // Add others
  people.forEach(person => {
    const theirHour = getCurrentHourInZone(person.timezone);
    const status = getWorkStatus(theirHour, person.workHourStart, person.workHourEnd);
    statusSummary[status].push(person.name);
  });

  return (
    <div className="card">
      <h3 className="font-semibold mb-4">✨ Work Overlap</h3>
      
      {/* Current Status Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {statusSummary.working.length > 0 && (
          <div className="p-3 bg-working/10 rounded-lg border border-working/30">
            <div className="text-sm text-working font-medium">🟢 Working</div>
            <div className="text-xs text-text-light-muted dark:text-text-muted mt-1">
              {statusSummary.working.join(', ')}
            </div>
          </div>
        )}
        
        {statusSummary.soon.length > 0 && (
          <div className="p-3 bg-soon/10 rounded-lg border border-soon/30">
            <div className="text-sm text-soon font-medium">🟡 Soon</div>
            <div className="text-xs text-text-light-muted dark:text-text-muted mt-1">
              {statusSummary.soon.join(', ')}
            </div>
          </div>
        )}
        
        {statusSummary.offline.length > 0 && (
          <div className="p-3 bg-offline/10 rounded-lg border border-offline/30">
            <div className="text-sm text-offline font-medium">🟠 Off</div>
            <div className="text-xs text-text-light-muted dark:text-text-muted mt-1">
              {statusSummary.offline.join(', ')}
            </div>
          </div>
        )}
        
        {statusSummary.sleeping.length > 0 && (
          <div className="p-3 bg-sleeping/10 rounded-lg border border-sleeping/30">
            <div className="text-sm text-sleeping font-medium">💤 Sleeping</div>
            <div className="text-xs text-text-light-muted dark:text-text-muted mt-1">
              {statusSummary.sleeping.join(', ')}
            </div>
          </div>
        )}
      </div>
      
      {/* Best Overlap Times */}
      {overlaps.length > 0 && (
        <div>
          <h4 className="text-sm text-text-light-muted dark:text-text-muted mb-2">📊 Best Times to Meet (your timezone)</h4>
          <div className="space-y-2">
            {overlaps.slice(0, 3).map((overlap, index) => {
              const medal = index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉';
              const startStr = overlap.start.toString().padStart(2, '0') + ':00';
              const endStr = overlap.end.toString().padStart(2, '0') + ':00';
              
              return (
                <div 
                  key={`${overlap.start}-${overlap.end}`}
                  className="flex items-center gap-3 p-2 bg-bg-light-card dark:bg-bg rounded-lg"
                >
                  <span>{medal}</span>
                  <span className="font-mono text-sm">{startStr} - {endStr}</span>
                  <span className="text-xs text-text-light-muted dark:text-text-muted">
                    {overlap.count} {overlap.count === 1 ? 'person' : 'people'} available
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {overlaps.length === 0 && (
        <p className="text-sm text-text-light-muted dark:text-text-muted">
          ⚠️ No good overlap found. Consider async communication!
        </p>
      )}
    </div>
  );
}
